import * as React from 'react';
import { useReactToPrint } from 'react-to-print';
import { ToolBarButton } from '../../designSystem/compositeComponents/ButtonToolBar';
import { PrintWrapper } from './PrintWrapper';

type PrintButtonWithContentProps = {
    documentTitle: string,
    children: () => React.ReactNode,
    tooltip?: string,
    printDelayMs?: number,
};

/**
 * Renders a toolbar print button. When clicked, mounts the child component
 * on demand, prints it, then unmounts it automatically after printing.
 *
 * The child is only in the DOM while printing is in progress.
 */
export function PrintButtonWithContent(props: PrintButtonWithContentProps): JSX.Element
{
    const [isPrinting, setIsPrinting] = React.useState(false);
    const printRef = React.useRef<HTMLDivElement | null>(null);
    const printDelayMs = props.printDelayMs ?? 200;

    const printFn = useReactToPrint({
        contentRef: printRef,
        documentTitle: props.documentTitle,
        onAfterPrint: () => setIsPrinting(false),
    });

    React.useEffect(() => {
        if (!isPrinting || !printRef.current)
            return;

        const timeout = window.setTimeout(() => printFn(), printDelayMs);
        return () => window.clearTimeout(timeout);
    }, [isPrinting, printDelayMs]);

    return (
        <div style={{ display: 'inline-flex' }}>
            <ToolBarButton
                icon='interface_actions_print'
                tooltip={props.tooltip ?? 'Print'}
                tooltipHorizontalAlignment='left'
                variant='text'
                onClick={() => setIsPrinting(true)}
            />
            {isPrinting &&
                <PrintWrapper innerRef={printRef}>
                    {props.children()}
                </PrintWrapper>
            }
        </div>
    );
}
